import React, { useState, useCallback } from 'react';
import SummaryTab from './SummaryTab';
import ModelStrengthsTab from './ModelStrengthsTab';
import TabNavigation from './components/shared/TabNavigation';
import TableView from './components/table/TableView';
import useResponsive from './hooks/useResponsive';
import useSwipeNavigation from './hooks/useSwipeNavigation';
import modelColumns from './data/modelColumns';
import benchmarkData from './data/benchmarkData';
import { calculateAverageScores, getModelStrengths } from './utils/dataUtils';
import { formatTaskName } from './utils/formatUtils';
import { getScoreColor } from './utils/styleUtils';

/**
 * Main component for displaying the MMLU benchmark data
 */
const MMluBenchmarkDisplay = () => {
  // Set up responsive layout handling
  const { 
    windowWidth, 
    isMobileView, 
    isChartCompact,
    getVisibleModels 
  } = useResponsive();
  
  // Set up state
  const [currentTab, setCurrentTab] = useState('summary');
  const [selectedModelIndex, setSelectedModelIndex] = useState(0);
  
  // Calculate average scores
  const averageScores = calculateAverageScores(modelColumns, benchmarkData);
  
  // Get visible models based on screen size
  const visibleModels = getVisibleModels(modelColumns, selectedModelIndex);
  
  // Configure swipe navigation for mobile - horizontal only for model selection
  const swipeHandlers = useSwipeNavigation({
    isMobileView,
    onSwipeLeft: () => {
      if (isMobileView) {
        setSelectedModelIndex(prev => 
          prev < modelColumns.length - 1 ? prev + 1 : prev
        );
      }
    },
    onSwipeRight: () => {
      if (isMobileView) {
        setSelectedModelIndex(prev => prev > 0 ? prev - 1 : prev);
      }
    },
    // Disabled vertical swipe navigation to prevent conflicts with normal scrolling
    onSwipeUp: () => {}, // Empty handler to prevent default behavior
    onSwipeDown: () => {} // Empty handler to prevent default behavior
  });

  return (
    <div 
      className="p-4 max-w-full overflow-hidden bg-white rounded-lg shadow"
      {...swipeHandlers}
    >
      <h1 className="text-2xl font-bold mb-4 text-center">Us Inc - Model Performance - MMLU</h1>
      
      {/* Tab Navigation */}
      <TabNavigation 
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
      />
      
      {/* Current Tab Content */}
      {currentTab === 'table' && (
        <TableView 
          benchmarkData={benchmarkData}
          modelColumns={modelColumns}
          averageScores={averageScores}
          isMobileView={isMobileView}
          visibleModels={visibleModels}
          getVisibleModels={getVisibleModels}
          selectedModelIndex={selectedModelIndex}
          setSelectedModelIndex={setSelectedModelIndex}
        />
      )}

      {/* Summary Tab */}
      {currentTab === 'summary' && (
        <SummaryTab 
          modelColumns={modelColumns}
          averageScores={averageScores}
          isMobileView={isMobileView}
          isChartCompact={isChartCompact}
        />
      )}

      {/* Model Strengths Tab */}
      {currentTab === 'strengths' && (
        <ModelStrengthsTab 
          getModelStrengths={() => getModelStrengths(benchmarkData, modelColumns)}
          getScoreColor={getScoreColor}
          formatTaskName={formatTaskName}
        />
      )}
    </div>
  );
};

export default MMluBenchmarkDisplay;
